// ── Deleting a record, and saying what goes with it ─────────────────────────
//
// Most deletes in ACE take one row. Some take a whole family: deleting a
// borrower removes its bank accounts and their ACH collections; deleting a
// covenant removes its calendar and every exception on it. There are no
// foreign keys in the database, so deleteRecord does that cleanup itself (see
// its CHILDREN map) — which means a click labelled "Delete this bank account?"
// can remove a dozen rows the person never saw.
//
// So the flow is two steps, and the second one is the point:
//
//   1. ASK. deleteRecord with `preview: true` runs every check and count the
//      real delete would run, and stops. It is the same code path, not a
//      second description of it, so the warning cannot promise something the
//      delete does not do.
//   2. TELL, then delete. If anything else is going, the open confirmation is
//      replaced by one that names it. If nothing else is going, the person has
//      already confirmed and is not asked twice.
//
// A refusal arrives during step 1 — an Account that still has loans under it,
// or a note only an administrator may remove. It is shown in the SAME dialog,
// which turns into a one-button explanation of why the delete cannot happen.
// One place to look: the prompt asks, and the prompt answers.
//
// This lives outside App.js so it can be exercised directly: a delete that
// silently takes more than it said is the failure this is written to prevent,
// and that is not something to verify by clicking around.

// The caller's own question ("Delete these terms?") has already been asked and
// answered by the time this runs. The second prompt is about the surprise, so
// it leads with the surprise.
export const cascadeMessage = (summary) =>
  `This also removes ${summary}. That cannot be undone — continue?`;

export const runDelete = async (
  { api, audit, setConfirm, setError, onUnauthorized },
  table, id, detail, onDone
) => {
  const fail = (e) => {
    if (e && e.message === "UNAUTHORIZED") { setConfirm(null); onUnauthorized(); return; }
    // A 409 (something is in the way) or 403 (a reserved note) lands here with
    // the server's sentence, which says what to do about it. It REPLACES the
    // prompt rather than closing it: the reason a delete cannot happen belongs
    // in the dialog the person is already looking at, in the place they were
    // expecting the result to appear.
    //
    // `acknowledge` makes it a dead end — one button, no destructive action.
    // Leaving the red Delete button under a sentence explaining that deleting
    // is impossible would invite a second click on an act already refused.
    setConfirm({
      title: "Cannot delete",
      msg: (e && e.message) || "Delete failed",
      acknowledge: true,
    });
  };

  const commit = async () => {
    try {
      const res = await api.post("deleteRecord", { table, id });
      // The audit line records what ACTUALLY went, from the delete's own
      // response — not from the preview, which is a forecast. The audit log is
      // the only lasting record that this took more than one row.
      const also = Object.entries((res && res.alsoDeleted) || {})
        .filter(([, n]) => n > 0).map(([t, n]) => `${n} ${t}`).join(", ");
      await audit("Deleted", table, id, (detail || "") + (also ? ` (also removed ${also})` : ""));
      setConfirm(null);
      if (onDone) onDone();
    } catch (e) { fail(e); }
  };

  setError(null);
  let pre;
  try {
    pre = await api.post("deleteRecord", { table, id, preview: true });
  } catch (e) { fail(e); return; }

  // An older API, or one that does not know this table, answers without a
  // summary. That is not a reason to refuse to delete — it is a reason not to
  // claim anything about what else goes.
  const summary = (pre && typeof pre.summary === "string") ? pre.summary.trim() : "";
  if (!summary) return commit();

  setConfirm({ msg: cascadeMessage(summary), onConfirm: commit });
};
